// webapp/src/lib/offlineTempIdRemap.js
//
// Swap a resolved offline temp id for its real server id inside the objects the
// UI is still holding.
//
// The offline write queue hands out negative temp ids (offlineWriteQueue's
// nextTempId) and the drawers put the synthesized record straight into the live
// store state — e.g. routes/dyrejournal/sale.js onClickNewVisitor does
// `newSale.visitors = [visitor]` and `newAnimal.owner = visitor.id`. When the
// queue drains, the record itself gets a real id, but those in-memory copies
// used to keep the temp id: the next save posts it to the server ONLINE (no
// drain, so no rewrite pass), and the temp id is persisted for good. That is how
// a sale, an animal and a journal ended up permanently pointing at customer
// -1787912423491 while the customer row itself had synced to id 478007.
//
// Temp ids are unique negative numbers, so replacing an exact value can never
// collide with a real (positive) id — no type dispatch needed, just swap the
// value wherever a known reference field holds it.
//
// Both helpers return the SAME reference when nothing matched, so callers can
// skip the store write (and the re-render) on a no-op.

export function remapObjectTempId(obj, fields, tempId, realId) {
    if (!obj || typeof obj !== 'object') return obj;
    let changed = false;
    const next = { ...obj };
    fields.forEach(field => {
        if (next[field] === tempId) {
            next[field] = realId;
            changed = true;
        }
    });
    return changed ? next : obj;
}

export function remapListTempId(list, fields, tempId, realId) {
    if (!Array.isArray(list) || list.length === 0) return list;
    let changed = false;
    const next = list.map(item => {
        const mapped = remapObjectTempId(item, fields, tempId, realId);
        if (mapped !== item) changed = true;
        return mapped;
    });
    return changed ? next : list;
}

/** Only a negative number is a temp id, and it must resolve to a real one. */
export function isResolvableTempId(tempId, realId) {
    return typeof tempId === 'number' && tempId < 0
        && typeof realId === 'number' && realId > 0;
}
